import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { candidateClosure, candidateClosureSubmit } from "model/CandidateModel";
import { AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import { buildUserAsyncFilterInitialOption } from "helpers/metadataFilterHelpers";

const CandidateClosureModal = ({
  isOpen,
  onClose,
  candidateId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState("");
  const [userLabel, setUserLabel] = useState("");
  const [closedDate, setClosedDate] = useState(() =>
    dayjs().format("YYYY-MM-DD")
  );

  useEffect(() => {
    if (!isOpen) return;
    setClosedDate(dayjs().format("YYYY-MM-DD"));
    setUserId("");
    setUserLabel("");
  }, [isOpen]);

  const handleUserChange = (option) => {
    setUserId(option?.value ?? "");
    setUserLabel(option?.label ?? "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = String(candidateId || "").trim();
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const normalizedUserId = String(userId || "").trim();
    if (!normalizedUserId) {
      toast.error("Please select a user");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        userId: normalizedUserId,
        closeDate: closedDate || dayjs().format("YYYY-MM-DD"),
      };
      const response = await candidateClosureSubmit(id, payload);
      if (response?.success || response?.isSuccess) {
        toast.success(response?.message || "Closure saved");
        const responseData =
          response?.data && typeof response.data === "object" ? response.data : {};
        onSuccess?.({
          id,
          candidateId: id,
          userId: normalizedUserId,
          closedDate: payload.closeDate,
          ...responseData,
        });
        onClose?.();
      } else {
        toast.error(response?.message || "Unable to save closure");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Add to closure</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          <FormGroup>
            <Label>Select user</Label>
            <AsyncUserSelect
              key={`candidate-closure-user-${candidateId || "new"}-${isOpen}`}
              pageName="Candidate"
              value={userId}
              initialOption={buildUserAsyncFilterInitialOption(userId, userLabel)}
              onChange={handleUserChange}
              placeholder="Select user"
            />
          </FormGroup>

          <FormGroup>
            <Label>Closed date</Label>
            <Input
              type="date"
              value={closedDate}
              onChange={(ev) => setClosedDate(ev.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Saving…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CandidateClosureModal;
